import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
} from "@mui/material";

import DropdownController from "../../components/inputs/DropdownController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { maxLengthMessage } from "../../utils/functions";

const checklistItemSchema = yup.object().shape({
	name: yup
		.string()
		.trim()
		.required("Item Name is required")
		.max(100, maxLengthMessage(100)),
	description: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
	inspectionType: yup.string().trim().nullable(),
});

const buildDefaults = (initialData) => ({
	name: initialData?.name || "",
	description: initialData?.description || "",
	inspectionType: initialData?.inspectionType
		? String(initialData.inspectionType)
		: "",
});

const AddEditChecklistItemDialog = ({
	open,
	onClose,
	onSave,
	initialData = null,
	viewOnly = false,
	inspectionTypeOptions = [],
}) => {
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(checklistItemSchema),
		defaultValues: buildDefaults(initialData),
	});

	useEffect(() => {
		reset(buildDefaults(initialData));
	}, [initialData, reset]);

	useEffect(() => {
		if (!open) {
			reset();
		}
	}, [open, reset]);

	const onSubmit = (data) => {
		const payload = {
			name: data.name?.trim() || null,
			description: data.description?.trim() || null,
			inspectionType: data.inspectionType || null,
		};

		onSave(payload);
		reset();
		onClose();
	};

	const dialogTitle = initialData
		? viewOnly
			? `View Checklist Item — ${initialData.name}`
			: `Edit Checklist Item — ${initialData.name}`
		: "Add New Checklist Item";

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent
				sx={{
					pointerEvents: viewOnly ? "none" : undefined,
					opacity: viewOnly ? 0.75 : undefined,
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					sx={{ pt: 1 }}
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "1fr",
							gap: 2,
						}}
					>
						<Box>
							<TextFieldController
								name="name"
								label="Item Name"
								control={control}
								errors={errors}
								placeholder="e.g. Thermal Camera"
								fullWidth
							/>
						</Box>
						<Box>
							<TextFieldController
								name="description"
								label="Description / Employee Guidance"
								control={control}
								errors={errors}
								placeholder="What the employee should check..."
								multiline
								rows={2}
								fullWidth
							/>
						</Box>
						<Box>
							<DropdownController
								name="inspectionType"
								label="Applies to Inspection Type"
								options={inspectionTypeOptions}
								control={control}
								errors={errors}
								placeholder="— All Inspection Types —"
							/>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5, display: "block" }}
							>
								Leave empty to include in every inspection type.
							</Typography>
						</Box>
					</Box>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				{!viewOnly && (
					<PATSaveButton
						onClick={handleSubmit(onSubmit)}
						tooltipText="Save"
					/>
				)}
				<PATCloseButton onClick={onClose} tooltipText="Close" />
			</DialogActions>
		</Dialog>
	);
};

export default AddEditChecklistItemDialog;
